import { EntityId } from 'entities-next/common'
import React, { useCallback, useEffect, useState } from 'react'
import { FormHelpers, FormManager } from 'utils-next/forms'

import { ApiScoutingCampaignData, ScoutingCampaign } from '../../../../../../../../entities-next/scoutingCampaign'
import { newScoutingCampaign } from '../../../../../../../../entities-next/scoutingCampaign/scoutingCampaign.helpers'
import {
  getApiScoutingCampaignCriteriaHandlers,
  ScoutingCampaignCriteriaDto,
} from '../../../../../../../../entities-next/scoutingCampaignCriteria'
import { TemporalEntityId } from '../../../../../../../../utils'
import { LoadingScreen } from '../../../../../../components-next/Loading'
import {
  parseScoutingCampaignFormDataToScoutingCampaign,
  parseScoutingCampaignToScoutingCampaignFormData,
  scoutingCampaignDataFormValidationSchema,
  ScoutingCampaignFormData,
} from './ScoutingCampaignDataForm.data'
import { ScoutingCampaignDataFormContent } from './ScoutingCampaignDataFormContent.component'
import { ScoutingCampaignNewFormContent } from './ScoutingCampaignNewFormContent.component'

export type ScoutingCampaignDataFormProps = {
  scoutingCampaign: ScoutingCampaign
  onSubmit: (scoutingCampaignId: EntityId, data: ApiScoutingCampaignData) => Promise<void>
  actionBarRef: React.MutableRefObject<any>
}

export async function copyTextToClipboard(text: string) {
  if ('clipboard' in navigator) {
    return await navigator.clipboard.writeText(text)
  } else {
    return document.execCommand('copy', true, text)
  }
}
export function ScoutingCampaignDataForm({ scoutingCampaign, onSubmit, actionBarRef }: ScoutingCampaignDataFormProps) {
  const submitHandler = useCorporateDataFormSubmitHandler(scoutingCampaign.id, onSubmit)
  const { apiGetScoutingCampaignCriteria } = getApiScoutingCampaignCriteriaHandlers()
  const [criteria, setCriteria] = useState<ScoutingCampaignCriteriaDto | null>(null)

  useEffect(() => {
    if (scoutingCampaign.id !== TemporalEntityId.new) {
      const retrieveData = async () => {
        if (scoutingCampaign.id) {
          setCriteria(await apiGetScoutingCampaignCriteria(scoutingCampaign.id))
        }
      }
      retrieveData()
    }
  }, [scoutingCampaign])

  if (scoutingCampaign.id !== TemporalEntityId.new && scoutingCampaign.id && !criteria) {
    return <LoadingScreen />
  }

  const initialValues: ScoutingCampaignFormData = {
    ...parseScoutingCampaignToScoutingCampaignFormData(
      scoutingCampaign && criteria ? { ...criteria, ...scoutingCampaign } : newScoutingCampaign()
    ),
  }

  return (
    <FormManager
      enableReinitialize={true}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={scoutingCampaignDataFormValidationSchema}
      initialValues={initialValues}
      onSubmit={submitHandler}
    >
      {(formikProps) =>
        initialValues.id === TemporalEntityId.new ? (
          <ScoutingCampaignNewFormContent actionBarRef={actionBarRef} {...formikProps} />
        ) : (
          <ScoutingCampaignDataFormContent actionBarRef={actionBarRef} {...formikProps} />
        )
      }
    </FormManager>
  )
}

function useCorporateDataFormSubmitHandler(corporateId: EntityId, onSubmit: ScoutingCampaignDataFormProps['onSubmit']) {
  return useCallback(
    async (data: ScoutingCampaignFormData, { setSubmitting }: FormHelpers<ScoutingCampaignFormData>) => {
      setSubmitting(true)
      const apiData = await parseScoutingCampaignFormDataToScoutingCampaign(data)
      await onSubmit(corporateId, apiData)
      setSubmitting(false)
    },
    [onSubmit, corporateId]
  )
}
